<template>
  <div class="home-page">
    <section class="welcome-panel glass-panel">
      <div>
        <p class="page-kicker">WELCOME BACK</p>
        <h1>{{ greeting }}，{{ displayName }}</h1>
      </div>
      <div class="clock-block">
        <strong>{{ timeText }}</strong>
        <span>{{ dateText }}</span>
      </div>
    </section>

    <section class="desktop-grid">
      <article
        class="ascii-card glass-panel"
        @mousemove="handlePointerMove"
        @mouseleave="handlePointerLeave"
        @click="shuffleGlyphs"
      >
        <div class="desktop-glow" :style="glowStyle"></div>
        <div class="scanline"></div>
        <div class="ascii-stage" aria-hidden="true">
          <span
            v-for="cell in cells"
            :key="cell.id"
            class="pixel-cell"
            :class="{ active: cell.energy > 0.58 }"
            :style="cellStyle(cell)"
          >
            {{ cell.char }}
          </span>
        </div>
      </article>

      <article class="tool-card glass-panel quote-card">
        <div class="tool-head">
          <span>今日短句</span>
          <button class="mini-btn" type="button" @click="nextQuote">换一句</button>
        </div>
        <p>{{ currentQuote }}</p>
      </article>

      <article class="tool-card glass-panel">
        <div class="tool-head">
          <span>反应力测试</span>
          <button class="mini-btn" type="button" @click="resetReaction">重来</button>
        </div>
        <button
          class="reaction-button"
          :class="`is-${reactionState}`"
          type="button"
          @click="handleReactionClick"
        >
          <span v-if="reactionState === 'idle'">开始</span>
          <span v-else-if="reactionState === 'ready'" class="ready-pulse">准备!</span>
          <span v-else-if="reactionState === 'go'">点我</span>
          <span v-else>完成</span>
        </button>
        <div class="tool-result">
          <strong>{{ reactionText }}</strong>
          <span v-if="reactionScore">{{ reactionScore }}</span>
        </div>
      </article>

      <article class="tool-card glass-panel">
        <div class="tool-head">
          <span>颜色敏感度测试</span>
          <button class="mini-btn" type="button" @click="resetColorTest">重开</button>
        </div>
        <div class="level-row">
          <span>第 {{ colorLevel }} / 10 关</span>
          <strong>{{ colorMessage }}</strong>
        </div>
        <div class="swatch-grid color-test-grid">
          <button
            v-for="swatch in swatches"
            :key="swatch.id"
            type="button"
            :class="{ picked: swatch.picked }"
            :style="{ background: swatch.color }"
            @click="pickSwatch(swatch)"
          ></button>
        </div>
      </article>

      <article class="tool-card glass-panel">
        <div class="tool-head">
          <span>下班计时</span>
          <button class="mini-btn" type="button" @click="saveOffWorkTime">保存</button>
        </div>
        <label class="offwork-form">
          <span>下班时间</span>
          <input type="time" v-model="offWorkTime" @change="saveOffWorkTime">
        </label>
        <div class="progress-shell">
          <i :style="{ width: offWorkProgress + '%' }"></i>
        </div>
        <strong class="progress-value">{{ offWorkRemainingText }}</strong>
        <span class="offwork-hint">{{ offWorkHint }}</span>
      </article>
    </section>
  </div>
</template>

<script>
const GLYPH_SETS = [
  ['.', ':', '+', '*', '#', '%', '@'],
  ['0', '1', '/', '\\', '|', '_', '='],
  ['·', '×', '◇', '◆', '□', '■', '▣'],
]

const QUOTES = [
  '今天也适合把小工具整理得顺手一点。',
  '少一点摩擦，多一点完成。',
  '界面安静一点，脑子就宽一点。',
  '把复杂的事压扁，再慢慢切开。',
]

const OFF_WORK_TIME_KEY = 'hf_off_work_time'

export default {
  name: 'HomePage',
  data() {
    return {
      now: new Date(),
      timer: null,
      reactionTimer: null,
      quoteIndex: 0,
      reactionState: 'idle',
      reactionStartedAt: 0,
      reactionMs: null,
      reactionMessage: '点开始，红色阶段忍住别动。',
      colorLevel: 1,
      colorMessage: '找出唯一不一样的色块',
      swatches: [],
      offWorkTime: '18:00',
      pointer: {
        active: false,
        x: 50,
        y: 50
      },
      seed: 0,
      columns: 18,
      rows: 10
    }
  },
  computed: {
    displayName() {
      try {
        const userInfo = JSON.parse(window.localStorage.getItem('userInfo') || '{}')
        return userInfo.userName || '朋友'
      } catch (error) {
        return '朋友'
      }
    },
    greeting() {
      const hour = this.now.getHours()
      if (hour < 6) return '夜深了'
      if (hour < 12) return '早上好'
      if (hour < 18) return '下午好'
      return '晚上好'
    },
    dateText() {
      return new Intl.DateTimeFormat('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'long'
      }).format(this.now)
    },
    timeText() {
      return new Intl.DateTimeFormat('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(this.now)
    },
    currentQuote() {
      return QUOTES[this.quoteIndex % QUOTES.length]
    },
    reactionScore() {
      if (this.reactionMs === null) return ''
      if (this.reactionMs <= 180) return '夯'
      if (this.reactionMs <= 240) return '顶级'
      if (this.reactionMs <= 320) return '人上人'
      if (this.reactionMs <= 450) return 'NPC'
      return '拉完了'
    },
    reactionText() {
      if (this.reactionMs !== null) {
        return `${this.reactionMs} ms`
      }
      return this.reactionMessage
    },
    offWorkTarget() {
      const [hour, minute] = (this.offWorkTime || '18:00').split(':').map(Number)
      const target = new Date(this.now)
      target.setHours(Number.isFinite(hour) ? hour : 18, Number.isFinite(minute) ? minute : 0, 0, 0)
      return target
    },
    offWorkDiffMs() {
      return this.offWorkTarget.getTime() - this.now.getTime()
    },
    offWorkRemainingText() {
      if (!this.offWorkTime) return '先输入下班时间'
      if (this.offWorkDiffMs <= 0) return '已到下班时间'
      return this.formatDuration(this.offWorkDiffMs)
    },
    offWorkHint() {
      if (!this.offWorkTime) return '时间会保存到本机浏览器'
      if (this.offWorkDiffMs <= 0) return '今天这班，理论上已经结束。'
      return `距离 ${this.offWorkTime} 还有这么久`
    },
    offWorkProgress() {
      if (!this.offWorkTime) return 0
      const start = new Date(this.now)
      start.setHours(9, 0, 0, 0)
      const total = Math.max(1, this.offWorkTarget.getTime() - start.getTime())
      const passed = this.now.getTime() - start.getTime()
      return Math.max(0, Math.min(100, Math.round((passed / total) * 100)))
    },
    cells() {
      const cells = []
      const glyphs = GLYPH_SETS[this.seed % GLYPH_SETS.length]
      const px = this.pointer.x
      const py = this.pointer.y

      for (let row = 0; row < this.rows; row += 1) {
        for (let col = 0; col < this.columns; col += 1) {
          const x = (col / Math.max(1, this.columns - 1)) * 100
          const y = (row / Math.max(1, this.rows - 1)) * 100
          const distance = Math.hypot(x - px, y - py)
          const ripple = Math.sin((row * 1.7 + col * 1.2 + this.seed * 5) / 2.4)
          const energy = this.pointer.active
            ? Math.max(0, 1 - distance / 34)
            : Math.max(0, (ripple + 1) / 10)
          const index = Math.min(glyphs.length - 1, Math.floor((energy * 5 + row + col + this.seed) % glyphs.length))

          cells.push({
            id: `${row}_${col}`,
            char: glyphs[index],
            energy,
            row,
            col
          })
        }
      }

      return cells
    },
    glowStyle() {
      return {
        '--mx': `${this.pointer.x}%`,
        '--my': `${this.pointer.y}%`
      }
    }
  },
  mounted() {
    this.offWorkTime = window.localStorage.getItem(OFF_WORK_TIME_KEY) || '18:00'
    this.resetColorTest()
    this.timer = window.setInterval(() => {
      this.now = new Date()
    }, 1000)
  },
  beforeDestroy() {
    window.clearInterval(this.timer)
    window.clearTimeout(this.reactionTimer)
  },
  methods: {
    nextQuote() {
      this.quoteIndex = (this.quoteIndex + 1) % QUOTES.length
    },
    handleReactionClick() {
      if (this.reactionState === 'idle' || this.reactionState === 'done') {
        this.startReaction()
        return
      }

      if (this.reactionState === 'ready') {
        window.clearTimeout(this.reactionTimer)
        this.reactionState = 'idle'
        this.reactionMs = null
        this.reactionMessage = '抢跑了，再来一次。'
        return
      }

      if (this.reactionState === 'go') {
        this.reactionMs = Math.max(0, Math.round(performance.now() - this.reactionStartedAt))
        this.reactionState = 'done'
        this.reactionMessage = '完成'
      }
    },
    startReaction() {
      window.clearTimeout(this.reactionTimer)
      this.reactionState = 'ready'
      this.reactionMs = null
      this.reactionStartedAt = 0
      this.reactionMessage = '准备!'
      const delay = 1000 + Math.floor(Math.random() * 3001)
      this.reactionTimer = window.setTimeout(() => {
        this.reactionState = 'go'
        this.reactionStartedAt = performance.now()
        this.reactionMessage = '现在点!'
      }, delay)
    },
    resetReaction() {
      window.clearTimeout(this.reactionTimer)
      this.reactionState = 'idle'
      this.reactionStartedAt = 0
      this.reactionMs = null
      this.reactionMessage = '点开始，红色阶段忍住别动。'
    },
    resetColorTest() {
      this.colorLevel = 1
      this.colorMessage = '找出唯一不一样的色块'
      this.buildColorRound()
    },
    buildColorRound() {
      const hue = Math.floor(Math.random() * 360)
      const saturation = 44 + Math.floor(Math.random() * 28)
      const lightness = 42 + Math.floor(Math.random() * 22)
      const delta = Math.max(2, 13 - this.colorLevel)
      const differentHue = (hue + (Math.random() > 0.5 ? delta : -delta) + 360) % 360
      const normalColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`
      const oddColor = `hsl(${differentHue}, ${saturation}%, ${lightness}%)`
      const oddIndex = Math.floor(Math.random() * 6)

      const idPrefix = `swatch_${Date.now()}`
      this.swatches = Array.from({ length: 6 }, (_, index) => ({
        id: `${idPrefix}_${index}`,
        color: index === oddIndex ? oddColor : normalColor,
        isDifferent: index === oddIndex,
        picked: false
      }))
    },
    pickSwatch(swatch) {
      this.swatches = this.swatches.map(item => ({
        ...item,
        picked: item.id === swatch.id
      }))

      if (!swatch.isDifferent) {
        this.colorMessage = '不是这个，再盯一眼。'
        return
      }

      if (this.colorLevel >= 10) {
        this.colorMessage = '通关，眼睛很锋利。'
        return
      }

      this.colorLevel += 1
      this.colorMessage = '正确，下一关更接近。'
      window.setTimeout(() => {
        this.colorMessage = '找出唯一不一样的色块'
        this.buildColorRound()
      }, 420)
    },
    saveOffWorkTime() {
      if (!this.offWorkTime) return
      window.localStorage.setItem(OFF_WORK_TIME_KEY, this.offWorkTime)
    },
    formatDuration(ms) {
      const totalSeconds = Math.max(0, Math.floor(ms / 1000))
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      return [hours, minutes, seconds]
        .map(value => String(value).padStart(2, '0'))
        .join(':')
    },
    handlePointerMove(event) {
      const rect = event.currentTarget.getBoundingClientRect()
      this.pointer = {
        active: true,
        x: Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)),
        y: Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100))
      }
    },
    handlePointerLeave() {
      this.pointer.active = false
    },
    shuffleGlyphs() {
      this.seed = (this.seed + 1) % GLYPH_SETS.length
    },
    cellStyle(cell) {
      const lift = cell.energy * 8
      const opacity = 0.26 + cell.energy * 0.72
      const scale = 1 + cell.energy * 0.55

      return {
        opacity,
        transform: `translateY(${-lift}px) scale(${scale})`
      }
    }
  }
}
</script>

<style scoped>
.home-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.welcome-panel {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-height: 136px;
  border-radius: 22px;
  padding: 26px;
}

.welcome-panel h1 {
  margin: 0;
  color: #101828;
  font-size: 34px;
  line-height: 1.2;
}

.clock-block {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  color: #667085;
  white-space: nowrap;
}

.clock-block strong {
  color: #101828;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 34px;
  letter-spacing: 0;
}

.clock-block span {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 700;
}

.desktop-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(260px, .8fr);
  gap: 14px;
}

.ascii-card,
.tool-card {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
}

.ascii-card {
  min-height: 360px;
  cursor: crosshair;
  isolation: isolate;
  grid-row: span 2;
  padding: 18px;
}

.desktop-glow {
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    radial-gradient(circle at var(--mx, 50%) var(--my, 50%), rgba(16, 24, 40, .14), transparent 28%),
    radial-gradient(circle at 18% 18%, rgba(220, 240, 255, .74), transparent 32%),
    radial-gradient(circle at 82% 78%, rgba(236, 230, 255, .66), transparent 30%);
}

.scanline {
  position: absolute;
  inset: 0;
  height: 34%;
  background: linear-gradient(180deg, transparent, rgba(16, 24, 40, .08), transparent);
  transform: translateY(-120%);
  animation: scan 5.2s linear infinite;
  pointer-events: none;
}

.ascii-stage {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(18, minmax(0, 1fr));
  grid-template-rows: repeat(10, minmax(0, 1fr));
  height: 100%;
  min-height: 324px;
}

.pixel-cell {
  display: grid;
  place-items: center;
  color: #101828;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: clamp(14px, 1.5vw, 22px);
  font-weight: 900;
  line-height: 1;
  text-shadow: 0 12px 28px rgba(15, 23, 42, .12);
  transition: transform .2s ease, opacity .2s ease, text-shadow .2s ease;
  user-select: none;
}

.pixel-cell.active {
  text-shadow:
    0 0 12px rgba(15, 23, 42, .22),
    0 14px 30px rgba(15, 23, 42, .18);
}

.tool-card {
  min-height: 172px;
  padding: 18px;
}

.tool-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: #101828;
  font-size: 14px;
  font-weight: 900;
}

.mini-btn {
  height: 30px;
  border: 1px solid rgba(15, 23, 42, .1);
  border-radius: 10px;
  background: rgba(255, 255, 255, .74);
  color: #344054;
  cursor: pointer;
  padding: 0 10px;
  font-weight: 800;
}

.quote-card p {
  margin: 28px 0 0;
  color: #344054;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.5;
}

.reaction-button {
  display: grid;
  width: 100%;
  height: 96px;
  margin-top: 20px;
  place-items: center;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 16px;
  background: #101828;
  color: #ffffff;
  cursor: pointer;
  font-size: 30px;
  font-weight: 900;
  transition: background .18s ease, transform .18s ease, box-shadow .18s ease;
}

.reaction-button.is-ready {
  background: #c1121f;
  box-shadow: 0 16px 34px rgba(193, 18, 31, .2);
}

.reaction-button.is-go {
  background: #079455;
  box-shadow: 0 16px 34px rgba(7, 148, 85, .2);
}

.reaction-button.is-done {
  background: #101828;
}

.ready-pulse {
  animation: readyPulse 1s ease-in-out infinite;
}

.tool-result,
.level-row,
.offwork-hint {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
  color: #667085;
  font-size: 12px;
  font-weight: 800;
}

.tool-result strong,
.level-row span {
  color: #101828;
}

.tool-result span,
.level-row strong {
  border-radius: 999px;
  background: rgba(16, 24, 40, .08);
  color: #101828;
  padding: 4px 8px;
}

.swatch-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.color-test-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.swatch-grid button {
  height: 46px;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 14px;
  cursor: pointer;
  transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease;
}

.swatch-grid button:hover,
.swatch-grid button.picked {
  border-color: rgba(16, 24, 40, .28);
  box-shadow: 0 10px 22px rgba(15, 23, 42, .12);
  transform: translateY(-1px);
}

.offwork-form {
  display: grid;
  gap: 8px;
  margin-top: 20px;
}

.offwork-form span {
  color: #667085;
  font-size: 12px;
  font-weight: 800;
}

.offwork-form input {
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid rgba(15, 23, 42, .1);
  border-radius: 12px;
  background: rgba(255, 255, 255, .72);
  color: #101828;
  font: inherit;
  font-weight: 800;
  padding: 0 12px;
}

.progress-shell {
  height: 18px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, .08);
  border-radius: 999px;
  background: rgba(255, 255, 255, .72);
  margin-top: 18px;
}

.progress-shell i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #101828, #667085);
  transition: width .24s ease;
}

.progress-value {
  display: block;
  margin-top: 14px;
  color: #101828;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", monospace;
  font-size: 22px;
}

.offwork-hint {
  justify-content: flex-start;
  margin-top: 8px;
}

@keyframes scan {
  to {
    transform: translateY(340%);
  }
}

@keyframes readyPulse {
  0% {
    opacity: .64;
    transform: scale(.82);
  }

  70% {
    opacity: 1;
    transform: scale(1.26);
  }

  100% {
    opacity: .64;
    transform: scale(.82);
  }
}

@media (max-width: 960px) {
  .welcome-panel,
  .desktop-grid {
    grid-template-columns: 1fr;
  }

  .welcome-panel {
    align-items: flex-start;
    flex-direction: column;
  }

  .clock-block {
    align-items: flex-start;
  }
}
</style>
